import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function Features() {
  const features = [
    {
      category: 'Lead Management',
      icon: '📊',
      color: '#1e40af',
      items: [
        {
          title: 'Lead Tracking Dashboard',
          description: 'Centralized dashboard to track all leads, their status, and interaction history in real-time.',
          icon: '📈'
        },
        {
          title: 'Lead Scoring',
          description: 'Automatically score and prioritize leads based on engagement and behavior patterns.',
          icon: '⭐'
        },
        {
          title: 'Lead Assignment',
          description: 'Automatically assign leads to team members based on territory, expertise, or availability.',
          icon: '👤'
        },
        {
          title: 'Pipeline Management',
          description: 'Visual pipeline to track leads through each stage of your sales process.',
          icon: '🔄'
        }
      ]
    },
    {
      category: 'Customer Communication',
      icon: '💬',
      color: '#3b82f6',
      items: [
        {
          title: 'Appointment Reminders',
          description: 'Automated SMS and email reminders for scheduled appointments to reduce no-shows.',
          icon: '📅'
        },
        {
          title: 'Follow-Up Notifications',
          description: 'Scheduled follow-up messages to keep customers engaged throughout their journey.',
          icon: '🔔'
        },
        {
          title: 'Order Updates',
          description: 'Real-time notifications for order status, shipping updates, and delivery confirmations.',
          icon: '📦'
        },
        {
          title: 'Account Alerts',
          description: 'Important account notifications, payment reminders, and service updates.',
          icon: '⚠️'
        }
      ]
    },
    {
      category: 'CRM Tools',
      icon: '🛠️',
      color: '#60a5fa',
      items: [
        {
          title: 'Contact Management',
          description: 'Store unlimited contacts with detailed profiles, notes, and interaction history.',
          icon: '👥'
        },
        {
          title: 'Activity Tracking',
          description: 'Log all customer interactions including calls, emails, meetings, and messages.',
          icon: '📝'
        },
        {
          title: 'Task Management',
          description: 'Create and assign tasks, set reminders, and track completion status.',
          icon: '✅'
        },
        {
          title: 'Document Storage',
          description: 'Securely store and organize customer documents, contracts, and files.',
          icon: '📄'
        }
      ]
    },
    {
      category: 'Analytics & Reporting',
      icon: '📊',
      color: '#93c5fd',
      items: [
        {
          title: 'Conversion Analytics',
          description: 'Track lead-to-customer conversion rates and identify bottlenecks in your funnel.',
          icon: '📈'
        },
        {
          title: 'Communication Metrics',
          description: 'Monitor message delivery rates, open rates, and customer engagement levels.',
          icon: '📊'
        },
        {
          title: 'Custom Reports',
          description: 'Generate custom reports with filters, date ranges, and export capabilities.',
          icon: '📑'
        },
        {
          title: 'Team Performance',
          description: 'Track individual and team performance metrics to optimize productivity.',
          icon: '🎯'
        }
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Features - Aramco Leads Pro CRM Platform</title>
        <meta name="description" content="Comprehensive CRM features including lead tracking, customer communication, appointment reminders, and analytics. A2P 10DLC compliant messaging." />
      </Helmet>

      <div style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', color: 'white', padding: '4rem 2rem', textAlign: 'center' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{ fontSize: '3rem', fontWeight: '800', marginBottom: '1rem' }}>Platform Features</h1>
          <p style={{ fontSize: '1.25rem', opacity: 0.95 }}>Everything you need to manage leads and communicate with customers</p>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '4rem 2rem' }}>
        {/* Hero Image */}
        <div style={{ marginBottom: '4rem', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}>
          <img 
            src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=400&fit=crop" 
            alt="CRM platform features and analytics" 
            style={{ width: '100%', height: '400px', objectFit: 'cover' }}
          />
        </div>

        {features.map((category, idx) => (
          <div key={idx} style={{ marginBottom: '5rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>{category.icon}</div>
              <h2 style={{ fontSize: '2.5rem', fontWeight: '800', color: category.color, marginBottom: '0.5rem' }}>
                {category.category}
              </h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
              {category.items.map((item, itemIdx) => (
                <div 
                  key={itemIdx}
                  style={{
                    background: 'white',
                    padding: '2rem',
                    borderRadius: '12px',
                    border: '2px solid #e5e7eb',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = category.color;
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = `0 8px 24px ${category.color}20`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>{item.icon}</div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.75rem' }}>
                    {item.title}
                  </h3>
                  <p style={{ color: '#6b7280', lineHeight: '1.6' }}>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Compliance Notice */}
        <div style={{ background: '#dbeafe', padding: '2rem', borderRadius: '12px', marginTop: '4rem', border: '2px solid #3b82f6' }}>
          <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e40af', marginBottom: '1rem' }}>
            ✅ 100% Compliant Communication
          </h3>
          <p style={{ color: '#1e3a8a', lineHeight: '1.8', marginBottom: '1rem' }}>
            All customer communication features are fully compliant with TCPA, CTIA, and A2P 10DLC regulations. 
            We enforce strict opt-in requirements and automated opt-out handling to ensure your business stays compliant.
          </p>
          <ul style={{ color: '#1e3a8a', lineHeight: '2', paddingLeft: '2rem' }}>
            <li>A2P 10DLC registered and approved</li>
            <li>Automatic STOP/UNSUBSCRIBE handling</li>
            <li>Opt-in documentation and tracking</li>
            <li>TCPA compliance monitoring</li>
            <li>Carrier-approved message templates</li>
          </ul>
        </div>

        {/* CTA */}
        <div style={{ 
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: 'white',
          padding: '3rem',
          borderRadius: '12px',
          textAlign: 'center',
          marginTop: '4rem'
        }}>
          <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '1rem' }}>
            Ready to Transform Your Lead Management?
          </h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '2rem', opacity: 0.95 }}>
            Start your free trial today and see how Aramco Leads Pro can help grow your business
          </p>
          <Link to="/contact" style={{
            display: 'inline-block',
            background: '#fbbf24',
            color: '#1f2937',
            padding: '1rem 2.5rem',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: '1.1rem',
            transition: 'all 0.3s ease'
          }}>
            Start Free Trial
          </Link>
        </div>
      </div>
    </>
  );
}
